import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Subject, EMPTY, Observable, merge } from 'rxjs';
import { UdtService, InterpolatedTemperatureResult } from 'src/app/shared/services/udt.service';
import { TotalDepthResult } from 'src/app/shared/models/trajectory.model';
import { handleValidationError } from 'src/app/shared/helpers/validation-error.helper';
import { ValidationRules } from 'src/app/shared/components/validated-number-input/validated-number-input.component';

interface DepthQuery {
  depth: number | null;
  depthType: 'tvd' | 'md';
}

@Component({
  selector: 'app-udt-temperature-interpolator',
  templateUrl: './udt-temperature-interpolator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UdtTemperatureInterpolatorComponent implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  @Input() totalDepth: TotalDepthResult | number;
  @Input() refresh$?: Observable<void>;
  @Input() depthUnit: string;
  @Input() tempUnit: string;

  public readonly interpolationForm = new FormGroup({
    interpolationDepth: new FormControl<number | null>(null, { updateOn: 'blur' }),
    interpolationDepthType: new FormControl<'tvd' | 'md'>('tvd')
  });

  public readonly depthTypes: { name: string; value: string }[] = [
    { name: 'TVD', value: 'tvd' },
    { name: 'MD', value: 'md' }
  ];

  public interpolatedTemp = signal<number>(0);
  public isLoading = signal<boolean>(false);
  public validationErrors = signal<string[]>([]);
  public maxDepth = signal<number | null>(null);
  public interpolationDepthValidation = signal<ValidationRules>({ min: 0, required: false });

  // ── Depth accessors ───────────────────────────────────────────────────────

  private get totalDepthTvd(): number {
    return typeof this.totalDepth === 'number'
      ? this.totalDepth
      : this.totalDepth?.trueVerticalDepth;
  }

  private get totalDepthMd(): number {
    return typeof this.totalDepth === 'number'
      ? this.totalDepth
      : this.totalDepth?.measuredDepth;
  }

  private depthForType(depthType: 'tvd' | 'md'): number {
    return depthType === 'tvd' ? this.totalDepthTvd : this.totalDepthMd;
  }

  // ── Form control accessors ────────────────────────────────────────────────

  get interpolationDepthControl(): FormControl<number | null> {
    return this.interpolationForm.controls.interpolationDepth;
  }

  constructor(private readonly _udtService: UdtService) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Seed the form with total depth TVD before the pipeline subscribes,
    // so the startWith emission carries the correct initial value.
    if (this.totalDepthTvd) {
      this.interpolationForm.patchValue(
        { interpolationDepth: this.totalDepthTvd },
        { emitEvent: false }
      );
    }

    // Build a single stream: form changes + external refresh triggers both
    // funnel through the same interpolation pipeline.
    const formChanges$ = this.interpolationForm.valueChanges;
    const refreshTrigger$ = this.refresh$ ?? EMPTY;

    merge(
      formChanges$.pipe(startWith(this.interpolationForm.value)),
      refreshTrigger$.pipe(map(() => this.interpolationForm.value))
    )
      .pipe(
        map(values => this.buildDepthQuery(values)),
        distinctUntilChanged(
          (prev, curr) => prev.depth === curr.depth && prev.depthType === curr.depthType
        ),
        switchMap(query => this.executeInterpolation(query)),
        takeUntil(this._destroy$)
      )
      .subscribe(result => this.handleTemperatureResult(result));
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // ── Public actions ────────────────────────────────────────────────────────

  public copyTemperature(): void {
    navigator.clipboard.writeText(this.interpolatedTemp().toString());
  }

  /** Re-runs the interpolation with the current form values. */
  public recalculate(): void {
    const query = this.buildDepthQuery(this.interpolationForm.value);
    this.executeInterpolation(query)
      .pipe(takeUntil(this._destroy$))
      .subscribe(result => this.handleTemperatureResult(result));
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Normalises raw form values into a DepthQuery, clamping the depth to the
   * valid [0, maxDepth] range and patching the form if clamping occurred.
   */
  private buildDepthQuery(values: Partial<{ interpolationDepth: number | null; interpolationDepthType: 'tvd' | 'md' }>): DepthQuery {
    const depthType = values.interpolationDepthType ?? 'tvd';
    const max = this.depthForType(depthType);

    this.updateMaxDepth(depthType, max);

    const raw = values.interpolationDepth;
    if (raw === null || raw === undefined) {
      return { depth: null, depthType };
    }

    const clamped = Math.max(0, Math.min(raw, max));
    if (clamped !== raw) {
      this.interpolationForm.patchValue(
        { interpolationDepth: clamped },
        { emitEvent: false }
      );
    }

    return { depth: clamped, depthType };
  }

  /**
   * Given a DepthQuery, calls the backend and handles loading/error state.
   * Returns EMPTY (and clears state) when depth is null.
   */
  private executeInterpolation(query: DepthQuery): Observable<InterpolatedTemperatureResult> {
    if (query.depth === null) {
      this.interpolatedTemp.set(0);
      this.validationErrors.set([]);
      return EMPTY;
    }

    this.isLoading.set(true);

    return this._udtService.interpolateTemperature(query.depth, query.depthType).pipe(
      catchError(err => {
        this.handleTemperatureError(err);
        return EMPTY;
      })
    );
  }

  private handleTemperatureResult(result: InterpolatedTemperatureResult): void {
    this.isLoading.set(false);
    this.validationErrors.set([]);
    this.interpolatedTemp.set(result?.temperature ?? 0);
  }

  private handleTemperatureError(err: unknown): void {
    this.isLoading.set(false);
    this.interpolatedTemp.set(0);
    handleValidationError(err, this.validationErrors, this.interpolationDepthControl);
  }

  private updateMaxDepth(depthType: 'tvd' | 'md', max: number): void {
    this.maxDepth.set(max);
    this.interpolationDepthValidation.set({
      min: 0,
      max: max ?? undefined,
      required: false
    });
  }
}